import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS, ROUTE_PATHS } from '@constants';
import { useStore } from '@hooks';
import SupportModal from '@pages/Home/SupportSection/components/SupportModal';
import { Button, Icon, Typography } from '@ui';
import { Flex } from 'antd';
import { observer } from 'mobx-react';
import { StyledButton } from './styled';

const EmptyState = observer(() => {
    const navigate = useNavigate();
    const store = useStore();
    const { isManualWithSyncInProgressOrFailed, isManualWithSyncInProgress, isManualWithSyncFailed, currentAccount } =
        store.accountsStore;
    const { hasPreLaunchedCampaign } = store.campaignsStore;
    const [isModalOpen, setIsModalOpen] = useState(false);

    if (isManualWithSyncInProgress) {
        const syncMethod = currentAccount?.getSyncMethod || '.CSV';
        return (
            <Flex vertical style={{ flexGrow: 1 }}>
                <Typography.Text data-e2e='campaigns_page_title' type='em0'>
                    Syncing your catalog from {syncMethod}...
                </Typography.Text>
                <Flex align='center' justify='center' style={{ flexGrow: 1 }}>
                    <Flex data-e2e='campaigns_empty-state_content' vertical align='center' gap={24}>
                        <Typography.Title data-e2e='campaigns_no-campaigns_text' level={2} color={COLORS.$gray60Black}>
                            We’re currently importing your product data to ensure your campaigns have the most
                            up-to-date information. Campaign creation will unlock automatically as soon as this process
                            is complete. This typically takes a few minutes, so feel free to check back shortly.
                        </Typography.Title>
                    </Flex>
                </Flex>
            </Flex>
        );
    }

    if (isManualWithSyncFailed) {
        const syncMethod = currentAccount?.getSyncMethod || '.CSV';
        const handleContactSupport = () => {
            setIsModalOpen(true);
        };
        return (
            <Flex vertical style={{ flexGrow: 1 }}>
                <Typography.Text data-e2e='campaigns_page_title' type='em0'>
                    Product sync failed
                </Typography.Text>
                <Flex align='center' justify='center' style={{ flexGrow: 1 }}>
                    <Flex data-e2e='campaigns_empty-state_content' vertical align='center' gap={24}>
                        <Typography.Title data-e2e='campaigns_no-campaigns_text' level={2} color={COLORS.$gray60Black}>
                            We were unable to import your catalog from {syncMethod}. Because your campaigns rely on
                            real-time product data, campaign creation is temporarily disabled.
                        </Typography.Title>
                        <StyledButton onClick={handleContactSupport} data-e2e='campaigns_contact-support_btn'>
                            <Flex align='center'>
                                <Icon.SupportIcon
                                    style={{
                                        marginRight: '8px',
                                        width: '24px',
                                        height: '24px',
                                        color: COLORS.$gray60Black
                                    }}
                                />
                                <Typography.Text type='badgeMedium' color={COLORS.$gray60Black}>
                                    Contact Support
                                </Typography.Text>
                            </Flex>
                        </StyledButton>
                    </Flex>
                </Flex>
                <SupportModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
            </Flex>
        );
    }

    return (
        <Flex vertical style={{ flexGrow: 1 }}>
            <Typography.Text data-e2e='campaigns_page_title' type='em0'>
                Campaigns
            </Typography.Text>
            <Flex align='center' justify='center' style={{ flexGrow: 1 }}>
                <Flex data-e2e='campaigns_empty-state_content' vertical align='center' gap={24}>
                    <Typography.Title data-e2e='campaigns_no-campaigns_text' level={2} color={COLORS.$gray60Black}>
                        You have no launched campaigns
                    </Typography.Title>
                    <Button
                        data-e2e='campaigns_add-new-campaign_btn'
                        onClick={() => navigate(ROUTE_PATHS.CAMPAIGNS_CREATE_STEP_1)}
                        type='primary'
                        size='large'
                        disabled={hasPreLaunchedCampaign || isManualWithSyncInProgressOrFailed}
                        iconRight={<Icon size='24px' color={COLORS.$white} component={() => <Icon.PlusIcon />} />}
                    >
                        Add New Campaign
                    </Button>
                </Flex>
            </Flex>
        </Flex>
    );
});

export default EmptyState;
